import './styles/global.css'
import './styles/fonts.css'
import 'react-toastify/dist/ReactToastify.css'

import { ThemeProvider } from '@mui/material'
import CssBaseline from '@mui/material/CssBaseline'
import { LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { AuthProvider } from 'app/auth'
import { initI18n } from 'app/i18n'
import { theme } from 'app/mui-theme'
import { useGlobalQueryClient } from 'app/query-client'
import { GlobalLoadingIndicator } from 'elements'
import { Loader } from 'elements/feedback'
import { ToastContainer } from 'elements/feedback/toast'
import React, { Suspense } from 'react'
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'
import ReactDOM from 'react-dom/client'
import { ErrorBoundary, FallbackProps } from 'react-error-boundary'
import { Helmet } from 'react-helmet'
import { BrowserRouter } from 'react-router-dom'

import reportWebVitals from './reportWebVitals'
import { App } from './root'

const LazyErrorPage = React.lazy(() => import('components/global/error-page'))

const AppEntry = () => {
  const globalQueryClient = useGlobalQueryClient()

  return (
    <Suspense fallback={<Loader centered />}>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <QueryClientProvider client={globalQueryClient}>
          <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
          <Helmet defaultTitle="Libraria" titleTemplate="%s · Libraria" />
          <ToastContainer />
          <GlobalLoadingIndicator />
          <AuthProvider>
            <BrowserRouter>
              <DndProvider backend={HTML5Backend}>
                <App />
              </DndProvider>
            </BrowserRouter>
          </AuthProvider>
        </QueryClientProvider>
      </LocalizationProvider>
    </Suspense>
  )
}

const ErrorBoundaryFallback = ({ error }: FallbackProps) => {
  return (
    <LazyErrorPage
      variant={error.message === 'SERVER_ERROR' ? 'SERVER_ERROR' : 'OTHER'}
    />
  )
}

const main = async () => {
  await initI18n()

  const container = document.querySelector('#root')
  const root = ReactDOM.createRoot(container!)

  root.render(
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ErrorBoundary FallbackComponent={ErrorBoundaryFallback}>
          <AppEntry />
        </ErrorBoundary>
      </ThemeProvider>
    </React.StrictMode>,
  )
}

void main()

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
